import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/Button";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/logo.png";

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { name: "Home", path: "/" },

    { name: "Services", path: "/services" },
    { name: "Stories", path: "/stories" },
    { name: "FAQ", path: "/faq" },
    { name: "About", path: "/about" },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Determine if we should show the logo (scrolled or non-homepage)
  const showLogo = !isHomePage || isScrolled;
  const navTextColor =
    isHomePage && !isScrolled ? "text-[#800020]" : "text-gray-700";
  const navHoverColor =
    isHomePage && !isScrolled ? "hover:text-white/80" : "hover:text-[#800020]";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isHomePage
          ? isScrolled
            ? "bg-white/95 backdrop-blur-sm shadow-md"
            : "bg-transparent"
          : "bg-white shadow-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
        {/* Logo - Show when scrolled on homepage or on other pages */}
        {showLogo && (
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src={logo}
              alt="BeginPack Ltd Logo"
              className="w-32 h-32 md:w-48 md:h-48 object-contain"
            />
          </Link>
        )}

        {/* Desktop Navigation - Centered on homepage, left-aligned on other pages */}
        <div
          className={`hidden md:flex items-center ${
            isHomePage
              ? "absolute left-1/2 transform -translate-x-1/2"
              : "ml-auto"
          } space-x-12`}
        >
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className={`font-jakarta text-lg font-medium transition-colors relative ${navTextColor} ${navHoverColor}`}
            >
              {item.name}
              {isActive(item.path) && (
                <motion.div
                  layoutId="activeNav"
                  className={`absolute -bottom-1 left-0 right-0 h-0.5 ${
                    isHomePage && !isScrolled ? "bg-white" : "bg-[#800020]"
                  }`}
                />
              )}
            </Link>
          ))}
          {/* <Button
            variant={"primary"}
            size="sm"
            rightIcon={<ArrowRight className="w-5 h-5" />}
          >
            Contact Us
          </Button> */}
        </div>

        {/* Mobile Menu Button - Always visible on mobile */}
        <div className="md:hidden z-50">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`p-3 rounded-lg transition-colors min-w-[48px] min-h-[48px] flex items-center justify-center ${
              isHomePage && !isScrolled
                ? "text-white bg-white/20 backdrop-blur-sm"
                : "text-gray-900 bg-gray-100 hover:bg-gray-200"
            }`}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{
              opacity: 0,
              height: 0,
            }}
            animate={{
              opacity: 1,
              height: "auto",
            }}
            exit={{
              opacity: 0,
              height: 0,
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={`md:hidden fixed inset-x-0 top-16 z-40 overflow-hidden ${
              isHomePage && isScrolled
                ? "bg-white/95 backdrop-blur-sm shadow-lg"
                : "bg-white shadow-lg"
            }`}
          >
            <div className="px-4 py-4 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`font-jakarta block px-4 py-3 text-base font-medium rounded-lg transition-all ${
                    isActive(item.path)
                      ? "text-[#800020] bg-[#800020]/10 font-semibold"
                      : "text-gray-700 hover:text-[#800020] hover:bg-gray-50"
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-current={isActive(item.path) ? "page" : undefined}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-4 px-4">
                <Button
                  className="w-full"
                  size="lg"
                  rightIcon={<ArrowRight className="w-5 h-5" />}
                  onClick={() =>
                    window.open("https://wa.me/2348138838732", "_blank")
                  }
                >
                  Contact Us
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
